import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import '../styles/Dashboard.css';
import Header from '../components/Header';
import {
  getUpaDistribution,
  getUpaWaitTimes,
  fetchUpasComStatus
} from '../server/Api';
import webSocketService from '../services/WebSocketService';
import RoutingService from '../services/RoutingService';

const CLASSIFICATION_LABELS = {
  'NAO_TRIADO': 'Não Triado',
  'AZUL': 'Não Urgente',
  'VERDE': 'Pouco Urgente',
  'AMARELO': 'Urgente',
  'LARANJA': 'Muito Urgente',
  'VERMELHO': 'Emergência'
};

function UpaStatsPage() {
  const { id } = useParams();
  const [upas, setUpas] = useState([]);
  const [distribution, setDistribution] = useState({});
  const [waitTimes, setWaitTimes] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingUpas, setLoadingUpas] = useState(true);
  const [error, setError] = useState(null);

  // Carrega a lista de UPAs
  useEffect(() => {
    async function loadUpas() {
      try {
        setLoadingUpas(true);
        const data = await fetchUpasComStatus();
        setUpas(data);
      } catch (err) {
        console.error('Erro ao carregar UPAs:', err);
      } finally {
        setLoadingUpas(false);
      }
    }
    loadUpas();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [distData, waitData] = await Promise.all([
          getUpaDistribution(id).catch(() => ({ distribution: {} })),
          getUpaWaitTimes(id).catch(() => ({ wait_times: {} }))
        ]);

        setDistribution(distData || { distribution: {} });
        setWaitTimes(waitData || { wait_times: {} });

      } catch (err) {
        console.error("Failed to load UPA data:", err);
        setError("Não foi possível carregar os dados da UPA");
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Inscreve-se para receber atualizações da UPA específica via WebSocket
    webSocketService.subscribeToUpa(id);

    // Escuta atualizações de fila em tempo real
    const unsubscribeQueue = webSocketService.onQueueUpdate((data) => {
      if (data.upaId === id) {
        // Atualiza distribuição
        setDistribution({
          upaId: id,
          distribution: {
            VERMELHO: { count: data.data.porClassificacao.vermelho || 0 },
            LARANJA: { count: data.data.porClassificacao.laranja || 0 },
            AMARELO: { count: data.data.porClassificacao.amarelo || 0 },
            VERDE: { count: data.data.porClassificacao.verde || 0 },
            AZUL: { count: data.data.porClassificacao.azul || 0 },
            NAO_TRIADO: { count: data.data.porClassificacao.semTriagem || 0 },
          },
          lastUpdated: data.data.ultimaAtualizacao,
        });

        // Atualiza tempos de espera
        setWaitTimes({
          upaId: id,
          wait_times: data.data.metricasPorClassificacao.map(m => ({
            classification: m.classificacao,
            average_wait_time_minutes: m.tempoMedioEsperaMinutos,
            max_protocol_wait_time: m.tempoMaximoEsperaProtocolo,
            patients_over_time: m.pacientesAcimaTempo,
          })),
          lastUpdated: data.data.ultimaAtualizacao,
        });
      }
    });

    return () => {
      unsubscribeQueue();
      webSocketService.unsubscribeFromUpa(id);
    };
  }, [id]);

  const upa = (upas || []).find(u => u.id === id) || {};

  // Mostra loading enquanto carrega UPAs ou dados
  if (loading || loadingUpas) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Carregando dados...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="upa-stats-container">
        <h2>{error}</h2>
        <Link to="/" className="back-link">Voltar ao Mapa</Link>
      </div>
    );
  }

  // Só mostra "UPA não encontrada" depois que as UPAs terminaram de carregar
  if (!upa.id) {
    return (
      <div className="upa-stats-container">
        <h2>UPA não encontrada.</h2>
        <Link to="/" className="back-link">Voltar ao Mapa</Link>
      </div>
    );
  }

  const getCardData = (classification) => {
    const classificationKey = Object.keys(CLASSIFICATION_LABELS).find(
      key => CLASSIFICATION_LABELS[key] === classification
    );
    const count = distribution.distribution?.[classificationKey]?.count || 0;
    const waitTimeItem = (waitTimes.wait_times || []).find(
      item => item.classification === classificationKey
    );
    const waitTime = waitTimeItem?.average_wait_time_minutes || 0;

    return {
      count: count,
      waitTime: Math.round(waitTime)
    };
  };
 

  const blueData = getCardData('Não Urgente');
  const greenData = getCardData('Pouco Urgente');
  const yellowData = getCardData('Urgente');
  const orangeData = getCardData('Muito Urgente');
  const redData = getCardData('Emergência');
  const triaData = getCardData('Não Triado');


  return (
    <>
      <Header />

      <div className="notification-banner">
        Se você estiver em emergência, procure a unidade mais próxima. Você é prioridade!
      </div>

      <div className="upa-stats-container">
        <header className="stats-header">
          <h1>{upa.name || 'UPA'}</h1>
          <p>{upa.address || 'Endereço não disponível'}</p>
          {distribution?.last_updated && (
            <p>Última atualização: {new Date(distribution.last_updated).toLocaleString()}</p>
          )}
        </header>

      <div className="stats-main">
        <div className="stats-card stats-card-triagem">
          <h2>Sem Triagem</h2>
          <p className="stats-value">{triaData.count}</p>
          <p className="stats-label">Pacientes aguardando</p>
          <p className="stats-wait">Tempo Médio: {RoutingService.formatMinutes(triaData.waitTime)}</p>
        </div>
        <div className="stats-card stats-card-blue">
          <h2>Azul</h2>
          <p className="stats-value">{blueData.count}</p>
          <p className="stats-label">Pacientes aguardando</p>
          <p className="stats-wait">Tempo Médio: {RoutingService.formatMinutes(blueData.waitTime)}</p>
        </div>

        <div className="stats-card stats-card-green">
          <h2>Verde</h2>
          <p className="stats-value">{greenData.count}</p>
          <p className="stats-label">Pacientes aguardando</p>
          <p className="stats-wait">Tempo Médio: {RoutingService.formatMinutes(greenData.waitTime)}</p>
        </div>

        <div className="stats-card stats-card-yellow">
          <h2>Amarelo</h2>
          <p className="stats-value">{yellowData.count}</p>
          <p className="stats-label">Pacientes aguardando</p>
          <p className="stats-wait">Tempo Médio: {RoutingService.formatMinutes(yellowData.waitTime)}</p>
        </div>

        <div className="stats-card stats-card-orange">
          <h2>Laranja</h2>
          <p className="stats-value">{orangeData.count}</p>
          <p className="stats-label">Pacientes aguardando</p>
          <p className="stats-wait">Tempo Médio: {RoutingService.formatMinutes(orangeData.waitTime)}</p>
        </div>

        <div className="stats-card stats-card-red">
          <h2>Vermelho</h2>
          <p className="stats-value">{redData.count}</p>
          <p className="stats-label">Pacientes aguardando</p>
          <p className="stats-wait">Tempo Médio: {RoutingService.formatMinutes(redData.waitTime)}</p>
        </div>
      </div>
    </div>
    </>
  );
}

export default UpaStatsPage;